/**
 * Phase 3 license-enforcement UI primitives — operator-visible badges +
 * tooltips that show the runtime clearance state for each surveillance
 * source. Pairs with @biomax/plugin-surveillance-feed's licenses module.
 *
 * Four clearance states map to four visual tokens:
 *   cleared              → green ✓   (verified + commercial OK)
 *   non-commercial       → orange 🚫 (verified but NC — paid mode excludes)
 *   partnership-required → violet 🔒 (mixed/ambiguous — needs agreement)
 *   unverified           → grey ?   (not yet read — fail-closed default)
 *
 * The fifth state (`unregistered`) shouldn't appear in practice (CI gate
 * catches it), but if it does, badge surfaces as a "registry gap" warning.
 */

import {
  type PaidTierClearance, getSourceClearance, getRequiredAttributions,
} from '@biomax/plugin-surveillance-feed';

interface ClearanceMeta {
  word: string;
  icon: string;
  color: string;
  bg: string;
  desc: string;
}

const META: Record<PaidTierClearance, ClearanceMeta> = {
  cleared:                { word: 'CLEARED',     icon: '✓',  color: 'var(--bm-state-live)',      bg: 'var(--bm-state-live-bg)',      desc: 'Verified license; commercial use permitted.' },
  'non-commercial':       { word: 'NON-COMMERCIAL', icon: '🚫', color: 'var(--bm-state-degraded)', bg: 'var(--bm-state-degraded-bg)', desc: 'Verified non-commercial; excluded from paid-tier output.' },
  'partnership-required': { word: 'PARTNERSHIP',  icon: '🔒', color: 'var(--bm-blocked)',         bg: 'var(--bm-state-blocked-bg)',   desc: 'Commercial-use rights ambiguous; needs a written agreement.' },
  unverified:             { word: 'UNVERIFIED',   icon: '?',  color: 'var(--bm-text-subtle)',     bg: 'var(--bm-state-idle-bg)',      desc: 'License page not yet read; fail-closed — treated as restricted.' },
  unregistered:           { word: 'UNREGISTERED', icon: '⚠',  color: 'var(--bm-state-rotting)',   bg: 'var(--bm-state-rotting-bg)',   desc: 'No entry in source-licenses.json — registry gap (CI bug).' },
};

export function LicenseBadge({ sourceId, small = false }: { sourceId: string; small?: boolean }) {
  const clearance = getSourceClearance(sourceId);
  const m = META[clearance];
  return (
    <span
      title={m.desc}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontFamily: 'var(--bm-font-mono)', fontSize: small ? 9.5 : 10.5,
        fontWeight: 700, letterSpacing: '.4px',
        color: m.color, background: m.bg,
        border: `1px solid ${m.color}55`, borderRadius: 4,
        padding: small ? '0px 5px' : '1px 6px', whiteSpace: 'nowrap',
      }}
    >
      <span aria-hidden>{m.icon}</span>
      {m.word}
    </span>
  );
}

export function getClearanceMeta(clearance: PaidTierClearance): ClearanceMeta {
  return META[clearance];
}

export type { PaidTierClearance };

/** Re-export the attribution collector — used by briefing/export surfaces
 *  (and the Phase-3 license-readiness tab) to render the required
 *  attribution lines for a given set of signals. */
export { getRequiredAttributions };
